import { useEffect, useState } from "react";
import api from "../services/api";
import { Button } from "../components/ui/button";
import {
  Loader2, RefreshCw, Download, Printer, CheckCircle2, XCircle, AlertTriangle,
  ServerCog, KeyRound, ExternalLink, FileText, ShieldCheck,
} from "lucide-react";

const STATUS_STYLE = {
  configured:  { color: "bg-emerald-100 text-emerald-700 border-emerald-200", icon: CheckCircle2, label: "Configured" },
  partial:     { color: "bg-amber-100 text-amber-700 border-amber-200", icon: AlertTriangle, label: "Partial" },
  placeholder: { color: "bg-amber-100 text-amber-700 border-amber-200", icon: AlertTriangle, label: "Placeholder" },
  empty:       { color: "bg-rose-100 text-rose-700 border-rose-200", icon: XCircle, label: "Empty" },
};

const ENV_EXAMPLES = {
  "MongoDB": `MONGO_URL=mongodb://localhost:27017
DB_NAME=test_database`,
  "JWT Secret": `# Generate with: python3 -c "import secrets; print(secrets.token_urlsafe(48))"
JWT_SECRET=<64-char-random-string>`,
  "Cloudinary": `CLOUDINARY_CLOUD_NAME=your-cloud
CLOUDINARY_API_KEY=123456789012345
CLOUDINARY_API_SECRET=XXXXXXXXXXXXXXXXXXXXXXXXX`,
  "Emergent LLM Key": `EMERGENT_LLM_KEY=sk-emergent-XXXXXXXXXXXX`,
  "Twilio (SMS + WhatsApp)": `TWILIO_ACCOUNT_SID=ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx
TWILIO_AUTH_TOKEN=xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx
TWILIO_FROM_SMS=+91XXXXXXXXXX
TWILIO_FROM_WHATSAPP=whatsapp:+91XXXXXXXXXX

# DLT templates must be pre-registered with TRAI before going live`,
  "Razorpay": `RAZORPAY_KEY_ID=rzp_live_XXXXXXXXXXXX
RAZORPAY_KEY_SECRET=XXXXXXXXXXXXXXXXXXXXXXXX
RAZORPAY_WEBHOOK_SECRET=XXXXXXXXXXXXXXXX
RAZORPAY_ACCOUNT_NUMBER=XXXXXXXXXXXX`,
  "Resend / SendGrid": `RESEND_API_KEY=re_XXXXXXXXXXXXXXXXXXXXXXXX
SENDER_EMAIL=notifications@pickngo.in`,
  "Delhivery": `DELHIVERY_API_TOKEN=XXXXXXXXXXXXXXXXXXXXXXXXXXXX
DELHIVERY_API_URL=https://track.delhivery.com/api
DELHIVERY_PICKUP_LOCATION=BLR_HSR_MAIN`,
  "Sentry (errors)": `SENTRY_DSN=https://abcdef123456@o123456.ingest.sentry.io/123456`,
  "Support team WhatsApp": `SUPPORT_TEAM_WHATSAPP=+919876543210`,
  "Google OAuth": `GOOGLE_CLIENT_ID=XXXXXX-XXXXXX.apps.googleusercontent.com
GOOGLE_CLIENT_SECRET=GOCSPX-XXXXXXXXXXXX`,
};

const PROVIDER_NOTES = {
  "Twilio (SMS + WhatsApp)": "Pickup OTPs require this. Without Twilio, agents cannot complete pickup. Apply for WhatsApp Business via Twilio console (2-7 day approval).",
  "Razorpay": "Currently using TEST keys (rzp_test_*). Customers can't actually pay until you switch to live keys + verify with a real ₹1 transaction.",
  "Cloudinary": "Already configured. Keep old credentials live for 24h when rotating to avoid breaking existing photo URLs.",
  "MongoDB": "Local pod restart wipes everything. Migrate to MongoDB Atlas for production OR set up a daily mongodump cron job.",
  "JWT Secret": "Rotating invalidates every active login. Plan rotation for low-traffic hours.",
  "Sentry (errors)": "Without it, errors only live in db.app_errors at /dashboard/system-health.",
  "Resend / SendGrid": "Drop in either provider's key into RESEND_API_KEY — the code is provider-agnostic.",
  "Delhivery": "Only needed if you want courier handoff. You can operate fully with in-house agents.",
  "Support team WhatsApp": "Receives AI-chat escalations. Needs Twilio configured to actually fire.",
  "Google OAuth": "Optional. Only needed if you want 'Sign in with Google' on the customer register page.",
};

export default function ProductionSetup() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/production/status");
      setData(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const print = () => window.print();
  const downloadChecklist = () => {
    if (!data) return;
    const lines = [
      `# PickNGo Production Setup Checklist`,
      `Generated ${new Date().toLocaleString()}`,
      ``,
      `## Summary`,
      `- Required configured: ${data.summary.required_configured} / ${data.summary.required_total}`,
      `- Required missing: ${data.summary.required_missing}`,
      `- Optional configured: ${data.summary.optional_configured} / ${data.summary.optional_total}`,
      `- Go-live ready: ${data.summary.go_live_ready ? "YES ✅" : "NO ❌"}`,
      ``,
      `## Status per integration`, ``,
    ];
    data.integrations.forEach((i) => {
      lines.push(`### ${i.name} — ${i.status.toUpperCase()}${i.required ? " (MANDATORY)" : " (OPTIONAL)"}`);
      lines.push(`Env file: \`${i.env_file}\``);
      if (i.signup_url) lines.push(`Get keys: ${i.signup_url}`);
      lines.push(`Cost: ${i.cost_estimate}`);
      if (i.env_keys_missing.length) lines.push(`Missing: ${i.env_keys_missing.join(", ")}`);
      if (i.env_keys_set.length) lines.push(`Configured: ${i.env_keys_set.join(", ")}`);
      lines.push(``);
    });
    const blob = new Blob([lines.join("\n")], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `pickngo-production-checklist-${Date.now()}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading || !data) {
    return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#FF6B00]" /></div>;
  }

  const { summary, integrations } = data;
  return (
    <div className="space-y-6 max-w-5xl mx-auto print:max-w-full" data-testid="production-setup-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 animate-fadeInUp print:hidden">
        <div>
          <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-2">
            <ServerCog className="h-7 w-7 text-[#FF6B00]" /> Production Setup
          </h1>
          <p className="text-sm text-slate-500 mt-1">Live status of every integration · what's set, what's missing, and how to fix it.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} className="gap-2"><RefreshCw className="h-4 w-4" /> Refresh</Button>
          <Button variant="outline" onClick={downloadChecklist} className="gap-2" data-testid="prod-download-md"><Download className="h-4 w-4" /> Checklist (.md)</Button>
          <Button onClick={print} className="bg-[#FF6B00] hover:bg-orange-600 text-white gap-2" data-testid="prod-print-pdf"><Printer className="h-4 w-4" /> Print / save as PDF</Button>
        </div>
      </div>

      {/* Go-live banner */}
      <div className={`rounded-2xl border p-5 flex items-center gap-4 animate-fadeInUp ${summary.go_live_ready ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`} data-testid="prod-go-live-banner">
        {summary.go_live_ready ? <ShieldCheck className="h-10 w-10 text-emerald-600 shrink-0" /> : <AlertTriangle className="h-10 w-10 text-amber-600 shrink-0" />}
        <div className="flex-1">
          <p className={`text-base font-bold ${summary.go_live_ready ? "text-emerald-900" : "text-amber-900"}`}>
            {summary.go_live_ready ? "Ready to go live" : `${summary.required_missing} mandatory integration${summary.required_missing === 1 ? "" : "s"} still need keys`}
          </p>
          <p className={`text-xs mt-0.5 ${summary.go_live_ready ? "text-emerald-700" : "text-amber-700"}`}>
            {summary.required_configured}/{summary.required_total} required configured · {summary.optional_configured}/{summary.optional_total} optional configured
          </p>
        </div>
      </div>

      {/* Tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 animate-fadeInUp print:break-after-page">
        <Tile label="Mandatory ready" value={`${summary.required_configured}/${summary.required_total}`} color="text-emerald-700" />
        <Tile label="Mandatory missing" value={summary.required_missing} color="text-rose-700" />
        <Tile label="Optional ready" value={`${summary.optional_configured}/${summary.optional_total}`} color="text-slate-700" />
        <Tile label="Status" value={summary.go_live_ready ? "GO" : "WAIT"} color={summary.go_live_ready ? "text-emerald-700" : "text-amber-700"} />
      </div>

      {/* Universal rules */}
      <div className="bg-white rounded-2xl border border-slate-100 p-5 animate-fadeInUp" data-testid="prod-rules">
        <h2 className="font-['Outfit'] text-lg font-semibold text-slate-900 flex items-center gap-2"><KeyRound className="h-5 w-5 text-[#FF6B00]" /> Universal rules</h2>
        <ol className="mt-3 text-sm text-slate-700 space-y-1.5 list-decimal pl-6">
          <li>All keys live in one file: <code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">/app/backend/.env</code></li>
          <li>Never edit <code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">/app/frontend/.env</code> — only <code>REACT_APP_BACKEND_URL</code> lives there.</li>
          <li>Never commit <code>.env</code> to GitHub. If you do, rotate every key immediately.</li>
          <li>After editing <code>.env</code>, restart the backend:
            <pre className="bg-slate-900 text-emerald-300 text-xs rounded-lg p-2 mt-1 font-mono">sudo supervisorctl restart backend</pre>
          </li>
          <li>No quotes around values. No spaces around <code>=</code>.</li>
          <li>Test in preview first, then promote keys to production.</li>
        </ol>
      </div>

      {/* Each integration */}
      {integrations.map((i, idx) => (
        <IntegrationCard key={i.name} integration={i} idx={idx} />
      ))}

      {/* Backup + uptime sections (not env-based) */}
      <ManualSection
        title="MongoDB backup — Disaster recovery"
        recommended
        body={[
          "Currently: no automated backup. A pod restart wipes everything.",
          "Option A: Use MongoDB Atlas (managed) — daily snapshots auto-enabled on M10+ tiers (~$57/mo).",
          "Option B: Self-hosted via cron — add this to root crontab:",
        ]}
        code={`# Daily backup at 03:00 UTC → /backups (keep last 7 days)
0 3 * * * mongodump --uri="$(grep MONGO_URL /app/backend/.env | cut -d= -f2)" \\
  --db=test_database --out=/backups/$(date +\\%Y\\%m\\%d) \\
  && find /backups -type d -mtime +7 -exec rm -rf {} +`}
      />

      <ManualSection
        title="Uptime monitoring"
        recommended
        body={[
          "Without monitoring, you find out about outages from angry customers.",
          "Recommended: UptimeRobot (free, 50 monitors) or Better Stack (free, 10 monitors).",
          "Endpoints to monitor:",
        ]}
        code={`GET https://<prod-domain>/api/health         # every 1 min
GET https://<prod-domain>/                    # every 5 min`}
      />

      <p className="text-[10px] text-slate-400 text-center print:hidden">
        Full setup guide also available at <code>/app/PRODUCTION_SETUP.md</code> on the server.
      </p>

      {/* Print-only styles */}
      <style>{`
        @media print {
          .print\\:hidden { display: none !important; }
          body, [data-testid="production-setup-page"] {
            background: white !important;
          }
          .animate-fadeInUp { animation: none !important; opacity: 1 !important; }
        }
      `}</style>
    </div>
  );
}

function IntegrationCard({ integration: i, idx }) {
  const style = STATUS_STYLE[i.status] || STATUS_STYLE.empty;
  const Icon = style.icon;
  const example = ENV_EXAMPLES[i.name];
  const note = PROVIDER_NOTES[i.name];
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 animate-fadeInUp print:break-inside-avoid" data-testid={`prod-int-${i.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <span className="w-8 h-8 bg-slate-100 rounded-xl flex items-center justify-center text-sm font-bold text-slate-600">{idx + 1}</span>
          <div>
            <h3 className="font-['Outfit'] text-lg font-bold text-slate-900 flex items-center gap-2">
              {i.name}
              <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase border ${i.required ? "bg-rose-100 text-rose-700 border-rose-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>
                {i.required ? "Mandatory" : "Optional"}
              </span>
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">{i.description}</p>
          </div>
        </div>
        <span className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-bold border ${style.color}`}>
          <Icon className="h-3.5 w-3.5" /> {style.label}
        </span>
      </div>

      <div className="mt-4 grid sm:grid-cols-2 gap-3 text-xs">
        <Detail label="Env file" value={<code className="text-slate-900">{i.env_file}</code>} />
        <Detail label="Cost estimate" value={i.cost_estimate} />
        {!!i.env_keys_set.length && <Detail label="Configured keys" value={<span className="text-emerald-700 font-mono text-[10px]">{i.env_keys_set.join(", ")}</span>} />}
        {!!i.env_keys_missing.length && <Detail label="Missing keys" value={<span className="text-rose-700 font-mono text-[10px]">{i.env_keys_missing.join(", ")}</span>} />}
      </div>

      {note && (
        <div className="mt-3 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2 text-xs text-amber-900">
          ⚠️ {note}
        </div>
      )}

      {i.signup_url && (
        <a href={i.signup_url} target="_blank" rel="noreferrer"
           className="inline-flex items-center gap-1 mt-3 text-xs text-blue-600 hover:underline">
          <ExternalLink className="h-3 w-3" /> Get keys at {i.signup_url.replace(/^https?:\/\//, "").split("/")[0]}
        </a>
      )}

      {example && (
        <details className="mt-3" data-testid={`prod-example-${i.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
          <summary className="text-xs font-semibold text-slate-700 cursor-pointer hover:text-slate-900">Show .env example (copy-paste-ready)</summary>
          <pre className="mt-2 bg-slate-900 text-emerald-300 text-xs rounded-lg p-3 font-mono overflow-x-auto whitespace-pre">{example}</pre>
          <p className="text-[10px] text-slate-400 mt-1">After saving, restart: <code>sudo supervisorctl restart backend</code></p>
        </details>
      )}
    </div>
  );
}

function ManualSection({ title, recommended, body, code }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 animate-fadeInUp print:break-inside-avoid">
      <h3 className="font-['Outfit'] text-lg font-bold text-slate-900 flex items-center gap-2">
        <FileText className="h-5 w-5 text-slate-500" /> {title}
        {recommended && <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase border bg-amber-50 text-amber-700 border-amber-200">Recommended</span>}
      </h3>
      <div className="mt-3 space-y-2 text-sm text-slate-700">
        {body.map((p, i) => <p key={`${title}-line-${i}`}>{p}</p>)}
        {code && <pre className="bg-slate-900 text-emerald-300 text-xs rounded-lg p-3 font-mono overflow-x-auto whitespace-pre">{code}</pre>}
      </div>
    </div>
  );
}

function Tile({ label, value, color = "text-slate-700" }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-3">
      <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">{label}</p>
      <p className={`text-2xl font-bold ${color} font-['Outfit']`}>{value}</p>
    </div>
  );
}
function Detail({ label, value }) {
  return (
    <div className="bg-slate-50 rounded-xl px-3 py-2">
      <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">{label}</p>
      <p className="mt-0.5">{value}</p>
    </div>
  );
}
